import React, { useState, useEffect } from 'react';
import { 
  X, 
  CheckCircle, 
  XCircle, 
  Clock, 
  CreditCard, 
  Calendar,
  User,
  DollarSign,
  FileText,
  Repeat,
  AlertCircle
} from 'lucide-react';
import { Assinatura } from '../types/Assinatura';
import { User as UserType } from '../types/User';
import { Pagamento } from '../types/Pagamento';
import { firebaseService } from '../services/firebaseService';
import { useAuth } from '../contexts/AuthContext';

interface AssinaturaModalProps {
  assinaturaId: string | null;
  onClose: () => void;
}

const AssinaturaModal: React.FC<AssinaturaModalProps> = ({ assinaturaId, onClose }) => {
  const { userProfile } = useAuth();
  const [assinatura, setAssinatura] = useState<Assinatura | null>(null);
  const [aluno, setAluno] = useState<UserType | null>(null);
  const [pagamentos, setPagamentos] = useState<Pagamento[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('detalhes');

  useEffect(() => {
    const fetchData = async () => {
      if (!assinaturaId || !userProfile?.empresaCnpj) {
        setLoading(false);
        return;
      }

      try {
          const assinaturaData = await firebaseService.getAssinatura(assinaturaId);
          
          if (assinaturaData) {
            setAssinatura(assinaturaData);
            
            // Buscar dados do aluno
            const alunosData = await firebaseService.getAlunos(userProfile.empresaCnpj);
            const alunoEncontrado = alunosData.find(a => a.id === assinaturaData.alunoId);
            setAluno(alunoEncontrado || null);
            
            // Buscar pagamentos relacionados
            const pagamentosData = await firebaseService.getPagamentosByAssinatura(
              userProfile.empresaCnpj, 
              assinaturaData.asaasSubscriptionId
            );
          setPagamentos(pagamentosData);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Erro ao buscar detalhes da assinatura:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [assinaturaId, userProfile]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800"><CheckCircle className="h-3 w-3" /> Ativa</span>;
      case 'INACTIVE':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800"><XCircle className="h-3 w-3" /> Inativa</span>;
      case 'OVERDUE':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800"><XCircle className="h-3 w-3" /> Vencida</span>;
      case 'PENDING':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3" /> Pendente</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getBillingTypeBadge = (type: string) => {
    switch (type) {
      case 'CREDIT_CARD':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800"><CreditCard className="h-3 w-3" /> Cartão</span>;
      case 'BOLETO':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">Boleto</span>;
      case 'PIX':
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">PIX</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{type}</span>;
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    // Verificar se é formato ISO ou DD/MM/YYYY
    let date;
    if (dateString.includes('T')) {
      date = new Date(dateString);
    } else {
      const [day, month, year] = dateString.split('/');
      date = new Date(`${year}-${month}-${day}`);
    }
    
    return date.toLocaleDateString('pt-BR');
  };

  const getCycleText = (cycle: string) => {
    switch (cycle) {
      case 'WEEKLY':
        return 'Semanal';
      case 'BIWEEKLY':
        return 'Quinzenal';
      case 'MONTHLY':
        return 'Mensal';
      case 'QUARTERLY':
        return 'Trimestral';
      case 'SEMIANNUALLY':
        return 'Semestral';
      case 'YEARLY':
        return 'Anual';
      default:
        return cycle;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!assinatura) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Assinatura não encontrada</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="flex items-center justify-center p-8">
            <AlertCircle className="h-12 w-12 text-red-500 mr-3" />
            <p className="text-gray-700">Não foi possível encontrar os detalhes desta assinatura.</p>
          </div>
          <div className="mt-6 flex justify-end">
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Detalhes da Assinatura</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            className={`px-6 py-3 text-sm font-medium ${
              activeTab === 'detalhes'
                ? 'border-b-2 border-primary-600 text-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('detalhes')}
          >
            Detalhes
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium ${
              activeTab === 'pagamentos'
                ? 'border-b-2 border-primary-600 text-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('pagamentos')}
          >
            Pagamentos
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'detalhes' ? (
            <div className="space-y-6">
              {/* Status e Valor */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-x-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <CreditCard className="h-6 w-6 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{assinatura.description}</h3>
                    <p className="text-sm text-gray-500">ID: {assinatura.asaasSubscriptionId}</p>
                  </div>
                </div>
                <div className="flex items-center gap-x-3">
                  {getStatusBadge(assinatura.status)}
                  <span className="text-xl font-bold text-gray-900">{formatCurrency(assinatura.value)}</span>
                </div>
              </div>

              {/* Informações do Aluno */}
              <div className="card p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center gap-x-3 mb-3">
                  <User className="h-5 w-5 text-gray-500" />
                  <h3 className="text-md font-medium text-gray-900">Informações do Aluno</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Nome</p>
                    <p className="text-sm font-medium text-gray-900">{aluno?.nome || 'Não disponível'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">ID do Aluno</p>
                    <p className="text-sm font-medium text-gray-900">{assinatura.alunoId}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-sm font-medium text-gray-900">{aluno?.email || 'Não disponível'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Telefone</p>
                    <p className="text-sm font-medium text-gray-900">{aluno?.telefone || 'Não disponível'}</p>
                  </div>
                </div>
              </div>

              {/* Detalhes da Assinatura */}
              <div className="card p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center gap-x-3 mb-3">
                  <FileText className="h-5 w-5 text-gray-500" />
                  <h3 className="text-md font-medium text-gray-900">Detalhes da Assinatura</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Forma de Pagamento</p>
                    <div className="mt-1">{getBillingTypeBadge(assinatura.billingType)}</div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Ciclo de Cobrança</p>
                    <div className="flex items-center gap-x-1 mt-1">
                      <Repeat className="h-4 w-4 text-gray-500" />
                      <p className="text-sm font-medium text-gray-900">{getCycleText(assinatura.cycle)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Próximo Vencimento</p>
                    <div className="flex items-center gap-x-1 mt-1">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <p className="text-sm font-medium text-gray-900">{formatDate(assinatura.nextDueDate)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Valor</p>
                    <div className="flex items-center gap-x-1 mt-1">
                      <DollarSign className="h-4 w-4 text-gray-500" />
                      <p className="text-sm font-medium text-gray-900">{formatCurrency(assinatura.value)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Data de Criação</p>
                    <div className="flex items-center gap-x-1 mt-1">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <p className="text-sm font-medium text-gray-900">{formatDate(assinatura.createDate)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Última Atualização</p>
                    <div className="flex items-center gap-x-1 mt-1">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <p className="text-sm font-medium text-gray-900">{formatDate(assinatura.updateDate)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Descrição */}
              <div className="card p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex items-center gap-x-3 mb-3">
                  <FileText className="h-5 w-5 text-gray-500" />
                  <h3 className="text-md font-medium text-gray-900">Descrição</h3>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-line">{assinatura.description}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Pagamentos Relacionados</h3>
              
              {pagamentos.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse table-auto">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vencimento</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pagamento</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {pagamentos.map((pagamento) => (
                        <tr key={pagamento.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{pagamento.codigo}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatCurrency(pagamento.valor)}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {pagamento.status === 'pago' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3" /> Pago
                              </span>
                            ) : pagamento.status === 'pendente' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                                <Clock className="h-3 w-3" /> Pendente
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                <XCircle className="h-3 w-3" /> {pagamento.status}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatDate(pagamento.dataVencimento)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {pagamento.dataPagamento ? formatDate(pagamento.dataPagamento) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                  <p>Nenhum pagamento encontrado para esta assinatura.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-x-3">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Fechar
          </button>
          {assinatura.status === 'ACTIVE' && (
            <button 
              className="btn btn-danger"
              onClick={() => {
                // Implementar cancelamento de assinatura
                if (window.confirm('Tem certeza que deseja cancelar esta assinatura?')) {
                  firebaseService.updateAssinaturaStatus(assinatura.id, 'INACTIVE')
                    .then(() => {
                      alert('Assinatura cancelada com sucesso!');
                      onClose();
                    })
                    .catch((error: any) => {
                      console.error('Erro ao cancelar assinatura:', error);
                      alert('Erro ao cancelar assinatura. Tente novamente.');
                    });
                }
              }}
            >
              Cancelar Assinatura
            </button>
          )}
          {assinatura.status !== 'ACTIVE' && (
            <button 
              className="btn btn-success"
              onClick={() => {
                // Implementar reativação de assinatura
                if (window.confirm('Deseja reativar esta assinatura?')) {
                  firebaseService.updateAssinaturaStatus(assinatura.id, 'ACTIVE')
                    .then(() => {
                      alert('Assinatura reativada com sucesso!');
                      onClose();
                    })
                    .catch((error: any) => {
                      console.error('Erro ao reativar assinatura:', error);
                      alert('Erro ao reativar assinatura. Tente novamente.');
                    });
                }
              }}
            >
              Reativar Assinatura
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssinaturaModal;
